import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { OrderForm } from '../components/trading/OrderForm';
import { OrderBook } from '../components/trading/OrderBook';
import { Tabs, TabPanel } from '../components/ui/Tabs';
import { ResizablePanel } from '../components/ui/ResizablePanel';
import { useKrakenMarket } from '../hooks/useKrakenMarket';
import { BalancesTable } from '../components/trading/BalancesTable';
import { PositionsTable } from '../components/trading/PositionsTable';
import { OpenOrdersTable } from '../components/trading/OpenOrdersTable';
import { TradesTable } from '../components/trading/TradesTable';
import { ClosedOrdersTable } from '../components/trading/ClosedOrdersTable';
import { TradingViewChart } from '../components/trading/TradingViewChart';
import { BarChart3, Activity } from 'lucide-react';
export function TradingPage() {
  const {
    ticker,
    bids,
    asks,
    trades,
    loading,
    error
  } = useKrakenMarket('XBTUSD', 5000);
  const [orderBookHeight, setOrderBookHeight] = useState(50); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [activeTab, setActiveTab] = useState('balances');
  const rightPanelRef = useRef<HTMLDivElement>(null);
  // Mock balance data
  const accountBalanceUSD = 79.19; // Account balance in USD (base currency)
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };
  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!rightPanelRef.current) return;
      const panelRect = rightPanelRef.current.getBoundingClientRect();
      const relativeY = e.clientY - panelRect.top;
      const percentage = relativeY / panelRect.height * 100;
      // Clamp between 20% and 80%
      const clampedPercentage = Math.min(Math.max(percentage, 20), 80);
      setOrderBookHeight(clampedPercentage);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);
  const spread = bids.length > 0 && asks.length > 0 ? asks[0].price - bids[0].price : undefined;
  const spreadPercent = spread && bids.length > 0 ? spread / bids[0].price * 100 : undefined;
  return <div className="flex h-screen bg-background-primary" data-id="element-1888">
      {/* Sidebar */}
      <Sidebar data-id="element-1889" />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1890">
        {/* Header */}
        <Header currentPrice={ticker?.price} indexPrice={ticker?.price ? ticker.price + 12.6 : 95453.7} priceChange={ticker?.change} priceChangePercent={ticker?.changePercent} volume24h={ticker ? `${(ticker.volume / 1000000).toFixed(0)}M` : '205M'} volume24hBTC={ticker ? `${(ticker.volume / (ticker.price * 1000)).toFixed(2)}K` : '2.15K'} accountBalance={accountBalanceUSD} data-id="element-1891" />

        {/* Trading Interface */}
        <div className="flex-1 flex overflow-hidden" data-id="element-1892">
          {/* Left Panel - Order Form (Resizable) */}
          <ResizablePanel defaultWidth={380} minWidth={360} maxWidth={600} side="left" data-id="element-1893">
            <div className="w-full border-r border-border-subtle overflow-y-auto" data-id="element-1894">
              <OrderForm currentPrice={ticker?.price} availableBalance={accountBalanceUSD} data-id="element-1895" />
            </div>
          </ResizablePanel>

          {/* Center Panel - Chart */}
          <div className="flex-1 flex flex-col" data-id="element-1896">
            {/* Chart Area */}
            <div className="flex-1 bg-background-secondary m-2 rounded overflow-hidden border border-border-subtle" data-id="element-1897">
              <TradingViewChart symbol="BITSTAMP:BTCUSD" theme="dark" interval="15" data-id="element-1898" />
            </div>

            {/* Bottom Panel - Positions/Balances */}
            <div className="h-64 border-t border-border-subtle bg-background-secondary" data-id="element-1899">
              <Tabs tabs={[{
              id: 'balances',
              label: 'Balances'
            }, {
              id: 'positions',
              label: 'Positions',
              badge: '1'
            }, {
              id: 'open-orders',
              label: 'Open orders',
              badge: '1'
            }, {
              id: 'conditional-orders',
              label: 'Conditional orders'
            }, {
              id: 'closed-orders',
              label: 'Closed orders'
            }, {
              id: 'trades',
              label: 'Trades'
            }]} defaultTab="balances" onTabChange={setActiveTab} data-id="element-1900" />
              <div className="h-[calc(100%-48px)]" data-id="element-1901">
                {activeTab === 'balances' && <BalancesTable data-id="element-1902" />}
                {activeTab === 'positions' && <PositionsTable data-id="element-1903" />}
                {activeTab === 'open-orders' && <OpenOrdersTable data-id="element-1904" />}
                {activeTab === 'conditional-orders' && <div className="flex flex-col items-center justify-center h-full" data-id="element-1905">
                    <div className="w-12 h-12 rounded-full bg-background-elevated flex items-center justify-center mb-3" data-id="element-1906">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-tertiary" data-id="element-1907">
                        <circle cx="12" cy="12" r="10" data-id="element-1908" />
                        <path d="M12 6v6l4 2" data-id="element-1909" />
                      </svg>
                    </div>
                    <p className="text-sm text-text-secondary" data-id="element-1910">
                      No conditional orders.
                    </p>
                  </div>}
                {activeTab === 'closed-orders' && <ClosedOrdersTable data-id="element-1911" />}
                {activeTab === 'trades' && <TradesTable data-id="element-1912" />}
              </div>
            </div>
          </div>

          {/* Right Panel - Order Book (Resizable) */}
          <ResizablePanel defaultWidth={380} minWidth={320} maxWidth={600} side="right" data-id="element-1913">
            <div ref={rightPanelRef} className="w-full h-full border-l border-border-subtle overflow-hidden relative" data-id="element-1914">
              <OrderBook bids={bids} asks={asks} spread={spread} spreadPercent={spreadPercent} trades={trades} style={{
              height: `${orderBookHeight}%`
            }} data-id="element-1915" />

              {/* Resize Handle */}
              <div onMouseDown={handleMouseDown} className={`absolute left-0 right-0 h-1 bg-border-subtle hover:bg-brand-purple cursor-ns-resize transition-colors z-10 ${isResizing ? 'bg-brand-purple' : ''}`} style={{
              top: `${orderBookHeight}%`
            }} data-id="element-1916">
                <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-1 bg-brand-purple rounded-full opacity-0 hover:opacity-100 transition-opacity" data-id="element-1917" />
              </div>

              {/* Bottom Section - Placeholder for future content */}
              <div className="absolute left-0 right-0 bottom-0 bg-background-primary overflow-y-auto" style={{
              top: `${orderBookHeight}%`,
              height: `${100 - orderBookHeight}%`
            }} data-id="element-1918">
                {/* Future: Recent trades, market depth chart, etc. */}
              </div>
            </div>
          </ResizablePanel>
        </div>
      </div>
    </div>;
}